import { Label } from "@/components/ui/label";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { MONEDAS, type Moneda } from "@/lib/informes";
import { comoSeGuardara, formatearMonto } from "@/lib/montos";
import { cn } from "@/lib/utils";

import { Campo } from "./CamposDeConstruccion";
import { valorComercialDeLaMaquina, type FormularioDeMaquina } from "./maquinaria";

type ClaveDelFormulario = keyof FormularioDeMaquina;

interface CamposDeMaquinaProps {
  /**
   * Prefijo de los `id` de los controles.
   *
   * EL FORMULARIO EN LÍNEA Y EL MODAL DE EDICIÓN CONVIVEN EN EL DOM. Con el
   * modal abierto, el formulario de la lista puede seguir montado detrás, así
   * que sin prefijos distintos habría dos controles con el mismo `id` y
   * `getByLabelText` (o cualquier tecnología asistiva) encontraría dos.
   */
  idPrefijo: string;
  formulario: FormularioDeMaquina;
  ocupada: boolean;
  escribir: (cambio: Partial<FormularioDeMaquina>) => void;
}

/**
 * Identificación: nombre, año de fabricación y unidades.
 *
 * `requerido` resalta en ámbar el nombre mientras esté vacío: es lo único que
 * el alta exige.
 */
export function GrupoDeIdentificacionDeMaquina({
  idPrefijo,
  formulario,
  ocupada,
  escribir,
  requerido = false,
}: CamposDeMaquinaProps & { requerido?: boolean }) {
  const campo = (clave: ClaveDelFormulario) => ({
    valor: formulario[clave] as string,
    conNota: false,
    disabled: ocupada,
    onChange: (valor: string) => escribir({ [clave]: valor }),
  });

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Campo
        id={`${idPrefijo}-nombre`}
        etiqueta="Nombre / tipo de equipo"
        placeholder="Ej: Tractor agrícola"
        clase="col-span-2"
        faltante={requerido && formulario.nombre.trim() === ""}
        {...campo("nombre")}
      />
      <Campo
        id={`${idPrefijo}-anio`}
        etiqueta="Año de fabricación"
        inputMode="numeric"
        placeholder="Ej: 2018"
        faltante={false}
        {...campo("anio_fabricacion")}
      />
      <Campo
        id={`${idPrefijo}-unidades`}
        etiqueta="Unidades"
        inputMode="numeric"
        placeholder="Ej: 2"
        faltante={false}
        {...campo("unidades")}
      />
    </div>
  );
}

/**
 * Valorización: vida útil total y residual, los dos valores UNITARIOS, el
 * ajuste comercial y la moneda.
 *
 * LA LECTURA ("Se guardará US$ 45.000") ES LO QUE HACE VISIBLE LA AMBIGÜEDAD
 * DEL PUNTO: "45.000" son cuarenta y cinco mil, pero quien copia de una
 * pantalla en inglés escribe "4.68" queriendo decir cuatro coma sesenta y
 * ocho. Se resuelve a la vista de la persona y no a sus espaldas.
 *
 * `columnas`: el formulario en línea las reparte en seis y el modal, más
 * angosto, en tres — es la única diferencia entre los dos.
 */
export function GrupoDeValorizacionDeMaquina({
  idPrefijo,
  formulario,
  ocupada,
  escribir,
  columnas = 6,
}: CamposDeMaquinaProps & { columnas?: 3 | 6 }) {
  const campo = (clave: ClaveDelFormulario) => ({
    valor: formulario[clave] as string,
    conNota: false,
    faltante: false,
    disabled: ocupada,
    onChange: (valor: string) => escribir({ [clave]: valor }),
  });

  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-3",
        columnas === 3 ? "lg:grid-cols-3" : "lg:grid-cols-6",
      )}
    >
      <Campo
        id={`${idPrefijo}-vida-total`}
        etiqueta="Vida útil total (años)"
        inputMode="numeric"
        placeholder="Ej: 15"
        {...campo("vida_util_total")}
      />
      <Campo
        id={`${idPrefijo}-vida-residual`}
        etiqueta="Vida útil residual (años)"
        inputMode="numeric"
        placeholder="Ej: 6"
        {...campo("vida_util_residual")}
      />
      <Campo
        id={`${idPrefijo}-reposicion`}
        etiqueta="Valor unitario reposición"
        inputMode="decimal"
        placeholder="Ej: 45.000"
        lectura={comoSeGuardara(formulario.valor_unitario_reposicion, formulario.moneda)}
        sufijoDeLectura=""
        {...campo("valor_unitario_reposicion")}
      />
      <Campo
        id={`${idPrefijo}-residual`}
        etiqueta="Valor unitario residual"
        inputMode="decimal"
        placeholder="Ej: 18.000"
        lectura={comoSeGuardara(formulario.valor_unitario_residual, formulario.moneda)}
        sufijoDeLectura=""
        {...campo("valor_unitario_residual")}
      />
      <Campo
        id={`${idPrefijo}-ajuste`}
        etiqueta="Ajuste comercial (%)"
        inputMode="decimal"
        placeholder="Ej: -10"
        {...campo("ajuste_comercial_pct")}
      />
      <div className="flex min-w-0 flex-col gap-1.5">
        <Label htmlFor={`${idPrefijo}-moneda`} className="mb-0">
          Moneda
        </Label>
        <NativeSelect
          id={`${idPrefijo}-moneda`}
          value={formulario.moneda}
          onChange={(e) => escribir({ moneda: e.target.value as Moneda })}
          disabled={ocupada}
        >
          {MONEDAS.map(({ valor, etiqueta }) => (
            <NativeSelectOption key={valor} value={valor}>
              {etiqueta}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
    </div>
  );
}

/**
 * El "Valor comercial estimado": lo que el servidor va a calcular con lo
 * escrito.
 *
 * La cuenta es la del servidor (`valorComercialDeLaMaquina`, su gemelo) y se
 * imprime CON EL SÍMBOLO de la moneda elegida: cada fila elige la suya y una
 * cifra desnuda no diría cuánto vale. Y el SIGNO se explica acá, donde se ve el
 * resultado.
 */
export function ValorComercialEstimado({ formulario }: { formulario: FormularioDeMaquina }) {
  const total = valorComercialDeLaMaquina(formulario);

  return (
    <div
      className={cn(
        "bg-card flex flex-wrap items-center gap-3.5 rounded-xl border-[1.5px] px-3.5 py-2.5",
        total === null ? "border-divisor" : "border-success-dot/40",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-ink-muted text-[0.65625rem] font-extrabold tracking-[0.06em] uppercase">
          Valor comercial estimado
        </div>
        <p className="text-ink-muted mt-0.5 text-[0.6875rem] font-semibold text-pretty">
          {total === null
            ? "Se calcula al completar el valor unitario residual. Un ajuste negativo descuenta."
            : "Unidades × valor unitario residual × (1 + ajuste). Un ajuste negativo descuenta."}
        </p>
      </div>
      <span
        className={cn(
          "flex-none font-mono text-lg font-bold",
          total === null ? "text-ink-ghost" : "text-ink",
        )}
      >
        {formatearMonto(total, formulario.moneda)}
      </span>
    </div>
  );
}
